import { toLower } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '~/eds/src/components/Box/Box';
import { ReactionCard } from '~/eds/src/components/Card/subcomponents/ReactionCard';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Section } from '~/eds/src/components/Section/Section';
import { Stepper } from '~/eds/src/components/Stepper/Stepper';
import { Text } from '~/eds/src/components/Text/Text';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import Alert from '~/ui/components/Alert/Alert';
import { openInNewTab } from '~/utils/browser';
import { testIsAdmin } from '~/utils/user';

import {
  NEGATIVE_LABELS,
  POSITIVE_LABELS,
  SIDE_BAR_WIDTH,
} from '../../constants';

const createSectionId = (index) => `sample-${index + 1}`;

const GuidedAiReview = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const client = useClientSubDomain();

  const isAdmin = testIsAdmin(currentUser);

  const guidedAiForm = useSelector(
    slices.automation.selectors.selectAutomationGuidedAiForm,
  );
  const allSamples = guidedAiForm?.samples ?? [];

  const [currentSectionId, setCurrentSectionId] = useState(createSectionId(0));

  const stepperDescription = (label) =>
    POSITIVE_LABELS.includes(toLower(label))
      ? intl.formatMessage({
          id: 'GuidedAiReview.fitsDescription',
          defaultMessage: 'Fits',
          description: 'Stepper description for positive label',
        })
      : intl.formatMessage({
          id: 'GuidedAiReview.doesNotFitDescription',
          defaultMessage: "Doesn't fit",
          description: 'Stepper description for negative label',
        });

  const availableSamples = allSamples
    .map((sample, index) => ({ ...sample, index }))
    .filter((sample) => !!sample.Text);

  const onViewDocument = (index) => {
    const evisortID = allSamples[index]?.evisortID ?? '';
    openInNewTab(`/${client}/document/${evisortID}`);
  };

  useEffect(() => {
    if (isAdmin) {
      availableSamples.every((sample, index) => {
        if (sample.Label === null) {
          const element = document.getElementById(createSectionId(index));
          element.scrollIntoView({ behavior: 'smooth' });
          setCurrentSectionId(createSectionId(index));
          return false;
        }
        return true;
      });
    }
  }, []);

  return (
    <Layout pb="20%">
      <Box position="fixed" w={SIDE_BAR_WIDTH} h="75%" overflowY="auto">
        <Stepper
          currentStepId={currentSectionId}
          name={intl.formatMessage({
            id: 'GuidedAiReview.stepperName',
            defaultMessage: 'guided ai review steps',
            description: 'Stepper name for guided ai review',
          })}
          steps={availableSamples.map((sample, index) => ({
            id: createSectionId(index),
            isCompleted: !!allSamples[sample.index]?.Label,
            label: intl.formatMessage(
              {
                id: 'GuidedAiReview.sampleTitle',
                defaultMessage: 'Sample {sampleNumber}',
                description: 'Stepper section title for sample',
              },
              { sampleNumber: sample.index + 1 },
            ),
            description: allSamples[sample.index]?.Label
              ? stepperDescription(allSamples[sample.index].Label)
              : null,
          }))}
        />
      </Box>
      <Box pl={SIDE_BAR_WIDTH}>
        {!isAdmin && (
          <Box pb="8">
            <Alert variant="warning" enableIcon>
              {intl.formatMessage(
                {
                  id: 'GuidedAiReview.accessWarning',
                  defaultMessage:
                    'You have access to {available} of {total} samples. Mark the samples below and then contact your admin to mark the remaining samples.',
                  description: 'Warning for limited sample access',
                },
                {
                  available: availableSamples.length,
                  total: allSamples.length,
                },
              )}
            </Alert>
          </Box>
        )}
        <Layout direction="column" preset="cards">
          {availableSamples?.map((fb, sectionIndex) => {
            const index = fb.index;
            return (
              <Section id={createSectionId(sectionIndex)}>
                <ReactionCard
                  actions={[
                    {
                      mode: 'icon',
                      icon: 'open-in-new',
                      level: 'tertiary',
                      text: intl.formatMessage({
                        id: 'GuidedAiReview.viewDocumentAction',
                        defaultMessage: 'View document',
                        description: 'Action text for viewing document',
                      }),
                      tooltip: intl.formatMessage({
                        id: 'GuidedAiReview.viewDocumentTooltip',
                        defaultMessage: 'View document',
                        description: 'Tooltip for viewing document',
                      }),
                      onClick: () => onViewDocument(fb.index),
                    },
                  ]}
                  isMulti={false}
                  reactions={[
                    {
                      type: 'thumbsup',
                      label: intl.formatMessage({
                        id: 'GuidedAiReview.reactionYes',
                        defaultMessage: 'Yes',
                        description: 'Thumbsup reaction label',
                      }),
                      value: POSITIVE_LABELS.includes(toLower(fb.Label)),
                    },
                    {
                      type: 'thumbsdown',
                      label: intl.formatMessage({
                        id: 'GuidedAiReview.reactionNo',
                        defaultMessage: 'No',
                        description: 'Thumbsdown reaction label',
                      }),
                      value: NEGATIVE_LABELS.includes(toLower(fb.Label)),
                    },
                  ]}
                  description={intl.formatMessage({
                    id: 'GuidedAiReview.sampleDescription',
                    defaultMessage: 'Does this sample represent your Clause?',
                    description: 'Description for sample reaction card',
                  })}
                  title={intl.formatMessage(
                    {
                      id: 'GuidedAiReview.sampleCardTitle',
                      defaultMessage: 'Sample {sampleNumber}',
                      description: 'Title for sample reaction card',
                    },
                    { sampleNumber: index + 1 },
                  )}
                  onReact={(reacts) => {
                    const element = document.getElementById(
                      createSectionId(sectionIndex + 1),
                    );
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                    setCurrentSectionId(createSectionId(sectionIndex + 1));
                    dispatch(
                      slices.automation.actions.setAutomationGuidedAiSampleLabel(
                        {
                          index,
                          reacts: reacts.map((r) => ({
                            ...r,
                            label: r.value
                              ? r.type === 'thumbsup'
                                ? 'yes'
                                : 'no'
                              : null,
                          })),
                        },
                      ),
                    );
                  }}
                >
                  <Text
                    id={createSectionId(sectionIndex)}
                    onClick={(e) => setCurrentSectionId(e.target.id)}
                  >
                    {fb.Text}
                  </Text>
                </ReactionCard>
              </Section>
            );
          })}
        </Layout>
      </Box>
    </Layout>
  );
};

export default GuidedAiReview;
